/**
 * Detect whether the app is currently running as an INSTALLED app rather than
 * in a normal browser tab. Covers:
 *   - Installed PWA (standalone / fullscreen / minimal-ui / window-controls-overlay)
 *   - Electron desktop app (EXE)
 *   - Android APK via Capacitor / Cordova / Trusted Web Activity (TWA)
 *   - iOS standalone (navigator.standalone)
 *
 * Used to hide the install popup and the "Download App" button once the user is
 * already inside an installed build.
 */
export const isRunningAsInstalledApp = () => {
  if (typeof window === 'undefined') return false;

  const nav = window.navigator || {};
  const ua = (nav.userAgent || '').toLowerCase();

  // Installed PWA display modes (Chrome/Edge/Safari desktop & mobile).
  const displayModes = [
    'standalone',
    'fullscreen',
    'minimal-ui',
    'window-controls-overlay',
  ];
  const inStandaloneDisplay = displayModes.some(
    (mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches
  );
  if (inStandaloneDisplay || nav.standalone === true) return true;

  // Electron desktop app (EXE).
  if (ua.includes('electron')) return true;
  if (window.electron || window.process?.versions?.electron) return true;

  // Capacitor / Cordova native shell (Android APK / iOS).
  if (window.Capacitor?.isNativePlatform?.() || window.Capacitor?.isNative) return true;
  if (window.cordova) return true;

  // Trusted Web Activity (Android APK wrapping the PWA).
  if (typeof document !== 'undefined' && document.referrer?.startsWith('android-app://')) return true;

  return false;
};


/**
 * Subscribe to install-state changes (appinstalled event + display-mode change).
 * Calls `onChange(true)` once the app becomes installed / standalone.
 * Returns an unsubscribe function.
 */
export const subscribeInstalledState = (onChange) => {
  if (typeof window === 'undefined') return () => {};

  const displayModeQuery = window.matchMedia?.('(display-mode: standalone)');
  const handleInstalled = () => onChange(true);
  const handleDisplayModeChange = (event) => {
    if (event.matches) onChange(true);
  };

  window.addEventListener('appinstalled', handleInstalled);
  displayModeQuery?.addEventListener?.('change', handleDisplayModeChange);

  return () => {
    window.removeEventListener('appinstalled', handleInstalled);
    displayModeQuery?.removeEventListener?.('change', handleDisplayModeChange);
  };
};

export const detectInstallBrowser = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/firefox|fxios/i.test(ua)) return 'firefox';
  if (/edg/i.test(ua)) return 'edge';
  if (/chrome|chromium|crios/i.test(ua) && !/edg/i.test(ua)) return 'chrome';
  if (/safari/i.test(ua) && !/chrome|chromium|crios/i.test(ua)) return 'safari';
  return 'unknown';
};

export const isMobileInstallDevice = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return window.innerWidth <= 768 || /android|iphone|ipad|ipod|mobile/i.test(ua);
};

export const getInstallInstructions = () => {
  const browser = detectInstallBrowser();
  const mobile = isMobileInstallDevice();

  if (browser === 'firefox' && mobile) {
    return {
      canPromptNatively: false,
      title: 'Install in Firefox',
      steps: [
        'Tap the menu button (three dots) in the address bar.',
        'Tap "Install" or "Add app to Home screen".',
        'Confirm to add ATOZASMail to your device.',
      ],
    };
  }

  if (browser === 'firefox') {
    return {
      canPromptNatively: false,
      title: 'Install in Firefox',
      steps: [
        'Click the menu button (three lines) in the top-right corner.',
        'Select "Install" if shown in the menu.',
        'If Install is not available, use Chrome or Edge for one-click install.',
      ],
    };
  }

  if (browser === 'safari' || /iphone|ipad|ipod/i.test(navigator.userAgent || '')) {
    return {
      canPromptNatively: false,
      title: 'Add to Home Screen',
      steps: [
        'Tap the Share button in Safari.',
        'Scroll and tap "Add to Home Screen".',
        'Tap "Add" to install ATOZASMail.',
      ],
    };
  }

  if (mobile) {
    return {
      canPromptNatively: false,
      title: 'Install ATOZASMail',
      steps: [
        'Open your browser menu.',
        'Choose "Install app" or "Add to Home screen".',
        'Confirm to install ATOZASMail.',
      ],
    };
  }

  return {
    canPromptNatively: true,
    title: 'Install ATOZASMail',
    steps: [
      'Click the install icon in the address bar, or',
      'Use the Install button below when it is available.',
    ],
  };
};
